import React, { useState, useEffect, useRef } from "react";
import VectorHauntedHouse from "./vectors/VectorHauntedHouse";
import VectorTombstone from "./vectors/VectorTombstone";
import VectorTreeHalloween from "./vectors/VectorTreeHalloween";
import VectorTreeHalloween2 from "./vectors/VectorTreeHalloween2";
import VectorGround from "./vectors/VectorGround";
import Star from "./vectors/Star";
import FlyingGhost, { setGhostSoundMuted, initOrUnlockAudio } from "./vectors/FlyingGhost";
import VectorGhost from "./vectors/VectorGhost";
import VectorVolumeMute from "./vectors/VectorVolumeMute";
import VectorVolumeUp from "./vectors/VectorVolumeUp";
import "./Themes.scss";

const MAX_STARS = 80;
const MAX_GHOSTS = 8;
const GHOST_COLORS = ["#FFFFFF"];
const GHOST_SCORE_STORAGE_KEY = "halloween-ghost-score";
const GHOST_MUTE_STORAGE_KEY = "halloween-ghost-muted";

const readStoredScore = (): number => {
    try {
        const raw = localStorage.getItem(GHOST_SCORE_STORAGE_KEY);
        const parsed = raw ? parseInt(raw, 10) : 0;
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    } catch {
        return 0;
    }
};

const readStoredMuted = (): boolean => {
    try {
        return localStorage.getItem(GHOST_MUTE_STORAGE_KEY) === "1";
    } catch {
        return false;
    }
};

export interface GhostConfig {
    id: number;
    top: number;
    size: number;
    duration: number;
    direction: number;
    color: string;
    delay?: number;
    lives: number;
    rainbow: boolean;
}

// Hệ số điểm cho ma có bóng mờ 7 màu (rainbow)
const RAINBOW_MULTIPLIER = 10;

// Số mạng ngẫu nhiên cho mỗi con ma (chỉ dùng khi ở focus mode) theo tỉ lệ:
// 5 mạng: 5%, 4 mạng: 10%, 3 mạng: 20%, 2 mạng: 30%, 1 mạng: 35%
const getGhostLives = () => {
    const roll = Math.random() * 100;
    if (roll <= 0.5) return 15;   // 0.5%
    if (roll <= 1) return 10;   // 1%
    if (roll <= 2) return 5;   // 2%
    if (roll <= 7) return 4;  // 5%
    if (roll <= 17) return 3;  // 10%
    if (roll <= 47) return 2;  // 30%
    return 1;                 // 53%
};

// Ma hiếm có bóng mờ 7 màu: 10% xuất hiện -> bắt được nhân 10 điểm
const getGhostRainbow = () => Math.random() < 0.1;
// const getGhostRainbow = () => Math.random() < 1;

interface StarConfig {
    id: number;
    left: number;
    top: number;
    delay: number;
}

const ThemeHalloween = ({ children }: { children?: React.ReactNode }) => {
    const [stars, setStars] = useState<StarConfig[]>([]);
    const [ghosts, setGhosts] = useState<GhostConfig[]>([]);
    const [score, setScore] = useState<number>(() => readStoredScore());
    const [isFocusMode, setIsFocusMode] = useState<boolean>(false);
    const [showCatchHint, setShowCatchHint] = useState<boolean>(false);
    const [isMuted, setIsMuted] = useState<boolean>(() => readStoredMuted());
    const isFocusModeRef = useRef<boolean>(false);
    const hintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cộng dồn điểm bắt ma vào localStorage
    useEffect(() => {
        try {
            localStorage.setItem(GHOST_SCORE_STORAGE_KEY, String(score));
        } catch {
            /* ignore write errors (private mode, quota, ...) */
        }
    }, [score]);

    // Đồng bộ trạng thái bật/tắt âm thanh với module FlyingGhost + localStorage
    useEffect(() => {
        setGhostSoundMuted(isMuted);
        try {
            localStorage.setItem(GHOST_MUTE_STORAGE_KEY, isMuted ? "1" : "0");
        } catch {
            /* ignore */
        }
    }, [isMuted]);

    // Keep a ref in sync so the ghost-spawning interval always reads the latest focus mode
    useEffect(() => {
        isFocusModeRef.current = isFocusMode;
    }, [isFocusMode]);

    // Ghost max size depends on focus mode:
    // - normal:      15 ~ 15 + 25
    // - focus mode:  38 ~ 38 + 50
    const getGhostSize = (focusMode: boolean) =>
        focusMode
            ? Math.floor(38 + Math.random() * 24)
            : Math.floor(15 + Math.random() * 24);

    useEffect(() => {
        const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
        const starCount = isMobile ? 32 : MAX_STARS;
        const maxGhostCount = isMobile ? 5 : MAX_GHOSTS;
        const initialGhostCount = isMobile ? 4 : 6;

        // Stars
        const initialStars: StarConfig[] = Array.from({ length: starCount }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            top: Math.random() * 75,
            delay: Math.random() * 4,
        }));
        setStars(initialStars);

        // Initial ghosts - staggered with negative delays to be visible immediately mid-flight
        const initialGhosts: GhostConfig[] = Array.from({ length: initialGhostCount }, (_, i) => ({
            id: Date.now() + i,
            top: 8 + Math.random() * 75,
            size: getGhostSize(isFocusModeRef.current),
            duration: 4 + Math.random() * 10,
            direction: Math.random() > 0.5 ? 1 : -1,
            color: GHOST_COLORS[Math.floor(Math.random() * GHOST_COLORS.length)],
            delay: -Math.random() * 6,
            lives: getGhostLives(),
            rainbow: getGhostRainbow()
        }));
        setGhosts(initialGhosts);

        // Spawn new ghosts periodically
        const ghostInterval = setInterval(() => {
            setGhosts((prevGhosts) => {
                if (prevGhosts.length >= maxGhostCount) return prevGhosts;
                const newGhost: GhostConfig = {
                    id: Date.now() + Math.random(),
                    top: 8 + Math.random() * 75,
                    size: getGhostSize(isFocusModeRef.current),
                    duration: 4 + Math.random() * 10,
                    direction: Math.random() > 0.5 ? 1 : -1,
                    color: GHOST_COLORS[Math.floor(Math.random() * GHOST_COLORS.length)],
                    delay: 0,
                    lives: getGhostLives(),
                    rainbow: getGhostRainbow()
                };
                return [...prevGhosts, newGhost];
            });
        }, 1200);

        return () => {
            clearInterval(ghostInterval);
        };
    }, []);

    // Dọn dẹp timer của hint khi unmount
    useEffect(() => {
        return () => {
            if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
        };
    }, []);

    const hideCatchHint = () => {
        if (hintTimeoutRef.current) {
            clearTimeout(hintTimeoutRef.current);
            hintTimeoutRef.current = null;
        }
        setShowCatchHint(false);
    };

    const toggleFocusMode = () => {
        initOrUnlockAudio();
        setIsFocusMode((prev) => {
            const next = !prev;
            if (next) {
                // Chỉ hiện hint cho người chơi lần đầu (localStorage chưa có điểm)
                if (readStoredScore() <= 0) {
                    // Vừa bật focus mode -> hiện hint, tự tan dần sau 5s
                    setShowCatchHint(true);
                    if (hintTimeoutRef.current) clearTimeout(hintTimeoutRef.current);
                    hintTimeoutRef.current = setTimeout(() => {
                        setShowCatchHint(false);
                        hintTimeoutRef.current = null;
                    }, 15000);
                }
            } else {
                hideCatchHint();
            }
            return next;
        });
    };

    const removeGhost = (id: number) => {
        setGhosts((prev) => prev.filter((ghost) => ghost.id !== id));
    };

    // points = số mạng của con ma vừa bắt được (mặc định 1 khi không truyền)
    const handleCatchGhost = (_id: number, points: number = 1) => {
        setScore((prev) => prev + points);
        // Bắt được 1 con ma đầu tiên -> ẩn hint ngay
        hideCatchHint();
    };

    const formatScore = (num: number) => {
        return num.toLocaleString("vi-VN");
    };

    return (
        <div className={`theme-vectors-container is-theme-halloween ${isFocusMode ? "is-focus-mode" : ""}`}>
            {isFocusMode ? (
                <div className="halloween-focus-bar">
                    <div className="halloween-focus-left">
                        {children}
                    </div>
                    <div className="halloween-focus-right">
                        {/* Score */}
                        <div className="halloween-score-box">
                            <VectorGhost iconSize={26} isCaught={true} /> <span key={score} className="score-number">{formatScore(score)}</span>
                        </div>

                        {/* Nút bật/tắt âm thanh */}
                        <button
                            type="button"
                            className="halloween-sound-btn"
                            aria-label={isMuted ? "Bật âm thanh" : "Tắt âm thanh"}
                            title={isMuted ? "Bật âm thanh" : "Tắt âm thanh"}
                            onClick={() => {
                                initOrUnlockAudio();
                                setIsMuted((m) => !m);
                            }}
                        >
                            {isMuted ? <VectorVolumeMute /> : <VectorVolumeUp />}
                        </button>

                        {/* Nút Quit */}
                        <button
                            type="button"
                            className="halloween-play-btn active"
                            onClick={toggleFocusMode}
                        >
                            Close
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {children}
                    {/* Catch the ghost button */}
                    <button
                        type="button"
                        className="halloween-play-btn"
                        onClick={toggleFocusMode}
                    >
                        Catch <VectorGhost color="#FFC896" isHit={true} iconSize={22} />
                    </button>
                </>
            )}

            <div className="nav-body-vectors">
                {/* Stars */}
                {stars.map((star) => (
                    <Star key={star.id} left={star.left} top={star.top} delay={star.delay} />
                ))}

                {/* Ground */}
                <VectorGround className="vector-ground" />

                {/* Bare Tree */}
                <VectorTreeHalloween className="vector-halloween-tree tree-left" />
                <VectorTreeHalloween2 className="vector-halloween-tree-2 tree-right" />

                {/* Haunted House */}
                <VectorHauntedHouse className="vector-haunted-house" />
                <VectorTombstone className="vector-tombstone-new" />
            </div>

            {/* Hint hướng dẫn khi bật focus mode */}
            {isFocusMode && (
                <div className={`halloween-catch-hint ${showCatchHint ? "visible" : ""}`}>
                    Click the ghost to catch
                </div>
            )}

            {/* Ghost layer */}
            <div className="flying-ghosts-layer">
                {ghosts.map((ghost) => (
                    <FlyingGhost
                        key={ghost.id}
                        id={ghost.id}
                        top={ghost.top}
                        size={ghost.size}
                        duration={ghost.duration}
                        direction={ghost.direction}
                        color={ghost.color}
                        delay={ghost.delay}
                        lives={ghost.lives}
                        rainbow={ghost.rainbow}
                        rainbowMultiplier={RAINBOW_MULTIPLIER}
                        isGameActive={isFocusMode}
                        onComplete={removeGhost}
                        onCatch={handleCatchGhost}
                    />
                ))}
            </div>
        </div>
    );
};

export default ThemeHalloween;
